import * as L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import { Api } from '@services';
import { useMapStore } from '../MapStore/UseMapStore';
import { getMapBubbleIcon, getMapIcon } from '../MapComponents/MapMiscComponents/MapIcons';
import { ObjectPopup } from '../MapComponents/MapMiscComponents/ObjectPopup';

// Helper to determine Azure Maps domain directly from host context
const getAzureMapsDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const isGov =
    hostname.endsWith('.gov') || hostname.includes('azure-gov') || hostname.includes('usgov');
  return isGov ? 'atlas.azure.us' : 'atlas.microsoft.com';
};

// Build (but do not add) an Azure Maps tile layer.
// Used when upgrading a static map, which has no Azure layer from initialization.
export async function createAzureLayer(theme: string) {
  const resp = await Api.get('/me/map-server-key');
  const mapKey = resp.data as string;
  if (!mapKey) throw new Error('No Azure Maps key returned');

  return (L.tileLayer as any).azureMaps({
    authOptions: {
      authType: 'subscriptionKey',
      subscriptionKey: mapKey,
      azMapsDomain: getAzureMapsDomain(),
    },
    tilesetId: theme === 'dark' ? 'microsoft.base.darkgrey' : 'microsoft.base.road',
    maxNativeZoom: 18,
    maxZoom: 25,
    minZoom: 2,
    crossOrigin: 'anonymous',
  });
}

// Check for valid map instance
export function isMapAlive(map: L.Map | null | undefined): boolean {
  return !!map && typeof (map as any).getPane === 'function';
}

// Check if leaflet layer is currently mounted to a map
export function isLayerMounted(layer: L.Layer | null | undefined): boolean {
  return !!(layer as any)?._map;
}

// Create icon clusters
export function createClusterLayer(type: string) {
  const clusterGroup = new L.MarkerClusterGroup({
    showCoverageOnHover: false,
    removeOutsideVisibleBounds: false,
    iconCreateFunction: (cluster) => {
      const markerSize = useMapStore.getState().markerSize;
      return createClusterIcon(cluster, type, markerSize);
    },
  });

  clusterGroup.on('clustermouseover', (e) => {
    handleClusterHover(e.layer);
  });
  clusterGroup.on('clustermouseout', (e) => {
    handleClusterHover(e.layer);
  });

  return clusterGroup;
}

// Handle cluster icon style
export function createClusterIcon(cluster, type, markerSize) {
  const children = cluster.getAllChildMarkers();
  const allOnline = children.every((item) => item.feature.properties.OnlineStatus === 'Online');
  const allOffline = children.every((item) => item.feature.properties.OnlineStatus === 'Offline');
  const allNoBeacons = children.every(
    (item) => item.feature.properties.OnlineStatus === 'NoBeacons',
  );

  if (allOnline) {
    return getMapBubbleIcon(type, '--c-primary', false, cluster, markerSize);
  } else if (allOffline) {
    return getMapBubbleIcon(type, '--danger', false, cluster, markerSize);
  } else if (allNoBeacons) {
    return getMapBubbleIcon(type, '--secondary', false, cluster, markerSize);
  } else {
    return getMapBubbleIcon(type, '--warning', false, cluster, markerSize);
  }
}

// Handle standard icon creation
export function createStandardIcon(feature, latlng, type, markerSize) {
  const marker = L.marker(latlng, {
    icon:
      feature.properties.OnlineStatus === 'Online'
        ? getMapIcon(type, '--c-primary', false, markerSize)
        : feature.properties.OnlineStatus === 'Offline'
        ? getMapIcon(type, '--danger', false, markerSize)
        : feature.properties.OnlineStatus === 'NoBeacons'
        ? getMapIcon(type, '--secondary', false, markerSize)
        : getMapIcon(type, '--warning', false, markerSize),
  });

  // After marker is added, set scale and hover
  marker.on('add', () => {
    const el = marker.getElement?.();
    if (!el) return;
    const container = el.querySelector('.marker-container') as HTMLElement;
    if (!container) return;

    // Attach hover listeners
    addBounceHover(container);
  });

  return marker;
}

// Update Marker Size
function addBounceHover(container: HTMLElement) {
  if (!container) return;

  const handleMouseEnter = () => {
    container.classList.add('has-shadow');
    container.classList.add('bounce');
    container.addEventListener('animationend', () => container.classList.remove('bounce'), {
      once: true,
    });
  };

  const handleMouseLeave = () => {
    container.classList.remove('has-shadow');
  };

  container.addEventListener('mouseenter', handleMouseEnter);
  container.addEventListener('mouseleave', handleMouseLeave);
}

// Update Marker Size
export function updateMarkerSizes(
  layerGroup: L.LayerGroup | L.MarkerClusterGroup | null,
  type: string,
  markerSize: number,
) {
  if (!layerGroup) return;

  layerGroup.eachLayer((layer: any) => {
    if (layer.setIcon && layer.feature) {
      const newIcon =
        layer.feature.properties.OnlineStatus === 'Online'
          ? getMapIcon(type, '--c-primary', false, markerSize)
          : layer.feature.properties.OnlineStatus === 'Offline'
          ? getMapIcon(type, '--danger', false, markerSize)
          : layer.feature.properties.OnlineStatus === 'NoBeacons'
          ? getMapIcon(type, '--secondary', false, markerSize)
          : getMapIcon(type, '--warning', false, markerSize);

      layer.setIcon(newIcon);

      const newElement = layer.getElement();
      const newContainer = newElement?.querySelector('.marker-container') as HTMLElement;
      if (newContainer) {
        addBounceHover(newContainer);
      }
    }
  });

  if ((layerGroup as any).refreshClusters) {
    (layerGroup as any).refreshClusters();
  }
}

// For single markers
export function handleStandardHover(layer: any) {
  if (!layer || typeof layer.on !== 'function') return;

  layer.on('add', () => {
    const el = layer.getElement?.();
    if (!el) return;

    const container = el.querySelector('.marker-container');
    addBounceHover(container);
  });
}

// For clusters
export function handleClusterHover(cluster: L.MarkerCluster) {
  if (!cluster || !cluster._icon) return;

  const el = cluster._icon;
  const container = el.querySelector('.marker-container');
  addBounceHover(container);
}

// Handle place hover
export function handlePlaceHover(layer) {
  layer.on('mouseenter', (e) => {
    layer.setStyle({ weight: 4 });
  });
  layer.on('mouseout', (e) => {
    layer.setStyle({ weight: 2 });
  });
}

// Handle item click
export function handleItemClick(
  feature,
  layer,
  type,
  showInfo,
  setSelected,
  handlePopupAndMenuOverlap,
  setClickEvent,
  setShowInfo,
  setShowMore,
) {
  layer.bindPopup(ReactDOMServer.renderToString(<ObjectPopup properties={feature.properties} />), {
    offset: L.point(0, -20),
  });

  layer.on('click', (e) => {
    setSelected(type);
    handlePopupAndMenuOverlap(layer, showInfo);
    setClickEvent(e.sourceTarget);

    document.getElementById(`copy-btn-${feature.properties.Id}`)?.addEventListener('click', () => {
      navigator.clipboard.writeText(e.sourceTarget.feature.properties.Name);
    });
    document.getElementById(`more-btn-${feature.properties.Id}`)?.addEventListener('click', () => {
      setSelected(type);
      setShowInfo(false);
      setShowMore(e.sourceTarget.feature);
      setClickEvent(e.sourceTarget);
    });
    document.getElementById(`go-to-btn-${feature.properties.Id}`)?.addEventListener('click', () => {
      window.location.href = `/assets/${feature.properties.Id}`;
    });
  });
}

// Handle map toggles
export function toggleLayer(map: L.Map, layer: L.Layer | null, condition: boolean) {
  if (!layer) return;
  const mounted = map.hasLayer(layer);
  if (condition && !mounted) layer.addTo(map);
  if (!condition && mounted) map.removeLayer(layer);
}

// Enables bounds layer editing
export function enableTransforms(boundsLayer) {
  const overlayOptions = {
    color: 'var(--secondary)',
    fillColor: '#ffffff00',
    weight: 1,
    dashArray: '0, 0',
  };

  try {
    boundsLayer.eachLayer((layer: any) => {
      layer?.transform?.enable?.({
        boundsOptions: overlayOptions,
        rotateHandleOptions: overlayOptions,
      });
      layer?.dragging?.enable?.();
    });
  } catch (err) {
    console.warn('Enable bounds transforms skipped:', err);
  }
}

// Disables bounds layer editing
export function disableTransforms(boundsLayer) {
  try {
    boundsLayer.eachLayer((layer: any) => {
      layer?.transform?.disable?.();
      layer?.dragging?.disable?.();
    });
  } catch (err) {
    console.warn('Disable bounds transforms skipped:', err);
  }
}

function isEditingOn(layer: any) {
  const e = layer?.editing;
  if (!e) return false;
  if (typeof e.enabled === 'function') return e.enabled();
  if (typeof e._enabled === 'boolean') return e._enabled;
  return !!e.enabled;
}

function isDraggingOn(layer: any) {
  const d = layer?.dragging;
  if (!d) return false;
  if (typeof d.enabled === 'function') return d.enabled();
  if (typeof d._enabled === 'boolean') return d._enabled;
  return !!d.enabled;
}

export function enableEditing(layer: L.Layer) {
  const any = layer as any;
  if (any.editing && typeof any.editing.enable === 'function' && !isEditingOn(any)) {
    any.editing.enable();
  }
  if (any.dragging && typeof any.dragging.enable === 'function' && !isDraggingOn(any)) {
    any.dragging.enable();
  }
}

export function disableEditing(layer: L.Layer) {
  const any = layer as any;
  if (any.editing && typeof any.editing.disable === 'function' && isEditingOn(any)) {
    any.editing.disable();
  }
  if (any.dragging && typeof any.dragging.disable === 'function' && isDraggingOn(any)) {
    any.dragging.disable();
  }
}

// Helper to detect overlap and pan if necessary
export function handlePopupAndMenuOverlap(layer, showInfo) {
  try {
    const map = layer._map;
    let latLng = layer.getLatLng ? layer.getLatLng() : layer.getBounds().getCenter();
    const newLatLng = map.project(latLng);
    if (!showInfo) {
      const adjustedLatLng = map.unproject([newLatLng.x + 250, newLatLng.y]);
      map.setView(adjustedLatLng, map.getZoom(), { animate: true });
    } else {
      const adjustedLatLng = map.unproject([newLatLng.x, newLatLng.y]);
      map.setView(adjustedLatLng, map.getZoom(), { animate: true });
    }
  } catch (e) {
    console.log(e);
  }
}

// Update overlay opacity
export function updateImageOpacity(value, imageLayer) {
  imageLayer.setOpacity(parseInt(value) / 100);
}

export function handleSliderChange(e: Event) {
  const input = e.target as HTMLInputElement;
  const newSize = Number(input.value);
  const scale = newSize / 40;

  document.querySelectorAll('.marker-container').forEach((el) => {
    const div = el as HTMLElement;
    div.style.setProperty('--marker-scale', scale.toString());
    div.style.transform = `scale(${scale})`;
  });
}

// Update Azure Maps Tile Layer dynamically
export function updateTileLayer(tilesetId: string, azureLayer: any, mapKey?: string) {
  if (!azureLayer) return;

  if (typeof azureLayer.setTilesetId === 'function') {
    azureLayer.setTilesetId(tilesetId);
    return;
  }

  if (typeof azureLayer.setOptions === 'function') {
    azureLayer.setOptions({ tilesetId });
    if (typeof azureLayer.redraw === 'function') {
      azureLayer.redraw();
    }
    return;
  }

  if (typeof azureLayer.setUrl === 'function' && mapKey) {
    const azMapsDomain = getAzureMapsDomain();
    const newUrl = `https://${azMapsDomain}/map/tile?subscription-key=${mapKey}&api-version=2.0&tilesetId=${tilesetId}&zoom={z}&x={x}&y={y}&tileSize=256&language=en-US&view=Auto`;
    azureLayer.setUrl(newUrl);
  }
}

// Update boundsLayer
export function updateBoundsLayer(border, boundsLayer) {
  for (let item in boundsLayer._layers) {
    boundsLayer._layers[item].setStyle({ opacity: border });
  }
}

// Translate coords for map img overlay
export function translateCoords(i, coords) {
  let imgCorner = L.latLng(coords[i][1], coords[i][0]);
  return imgCorner;
}

// Upgrade non geospatial map to geospatial
export function startUpgrade(
  latitude,
  longitude,
  mapGeoJSON,
  mapInstance,
  boundsLayer,
  imageLayer,
  azureLayer,
  setSelected,
  setUpgradeInProg,
  setOverlayFC,
) {
  if (latitude == null || longitude == null) return;
  if (!mapInstance || !boundsLayer || !imageLayer) return;

  mapGeoJSON.properties.MapType = 'Geospatial';
  setSelected('overlay');

  for (let item in boundsLayer._layers) {
    let center = boundsLayer._layers[item].getCenter();
    let points = boundsLayer._layers[item].getLatLngs();
    let offsets = [];

    let scaleFactor = Math.cos((center.lat * Math.PI) / 180) / Math.cos((latitude * Math.PI) / 180);

    points[0].forEach((elem) => {
      let point = [];
      point.push(elem.lat - center.lat);
      point.push((elem.lng - center.lng) * scaleFactor);
      offsets.push(point);
    });
    mapInstance.setView([latitude, longitude]);
    let newPoints = [];
    for (var i = 0; i < offsets.length; i++) {
      var point = [];
      point.push(latitude + offsets[i][0]);
      point.push(longitude + offsets[i][1]);
      newPoints.push(point);
    }
    boundsLayer._layers[item].setLatLngs(newPoints);

    imageLayer.reposition(
      boundsLayer._layers[item]._latlngs[0][3],
      boundsLayer._layers[item]._latlngs[0][2],
      boundsLayer._layers[item]._latlngs[0][0],
    );
  }

  // Only show Azure tiles once the map has moved to the chosen location,
  // and keep them underneath the image overlay and bounds polygon
  if (azureLayer && !mapInstance.hasLayer(azureLayer)) {
    mapInstance.addLayer(azureLayer);
    azureLayer.bringToBack?.();
  }

  setUpgradeInProg(true);
  setOverlayFC(boundsLayer.toGeoJSON());
}
